import React, { useRef, useState, ChangeEvent, KeyboardEvent } from "react";
import axios from "axios";
import axiosInstance from "utils/axiosInstance";
import { PRESIGNED_URL } from "constants/api";
import { v4 as uuidv4 } from "uuid";
import { useField } from "formik";
import { FiImage } from "react-icons/fi";
import { Spinner } from "components/ui/BootstrapCompat";
import { COLOR_APP, URL_PHOTO_AW3 } from "constants/index";

interface UploadImageFormikProps {
  rootStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  commentText?: string;
  name: string;
  [key: string]: any;
}

const acceptedImageTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/heic",
  "image/heif",
].join(",");

const getFileType = (file: File) => {
  if (file.type) return file.type;

  const extension = file.name.split(".").pop()?.toLowerCase();
  const fallbackTypes: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
  };

  return fallbackTypes[extension || ""] || "application/octet-stream";
};

const isAllowedImage = (file: File) => {
  const fileType = getFileType(file);
  return (
    fileType.startsWith("image/") ||
    /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)
  );
};

const UploadImageFormik: React.FC<UploadImageFormikProps> = ({
  rootStyle,
  style,
  commentText = "JPG, JPEG, PNG, WEBP",
  ...other
}) => {
  const [_, meta, helpers] = useField(other.name);
  const { value } = meta;
  const { setTouched, setValue } = helpers;
  const showError = Boolean(meta.touched && meta.error);

  const inputRef = useRef<HTMLInputElement>(null);
  const componentIdRef = useRef(other.id || other.name || uuidv4());
  const [imageLoading, setImageLoading] = useState<string | number>("");
  const [uploadError, setUploadError] = useState("");

  const imageSrc =
    typeof value === "string" && value.startsWith("http")
      ? value
      : URL_PHOTO_AW3 + value;

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      resetInput();
      return;
    }

    setTouched(true);
    setUploadError("");
    setImageLoading("");

    try {
      if (!isAllowedImage(file)) {
        setUploadError("ຮອງຮັບຮູບ JPG, PNG, WEBP, HEIC ຫຼື HEIF ເທົ່ານັ້ນ");
        return;
      }

      const fileType = getFileType(file);
      const responseUrl = await axiosInstance.post(PRESIGNED_URL, {
        name: fileType,
        fileName: file.name,
        fileType,
      });

      setImageLoading(1);
      const afterUpload = await axios.put(responseUrl.data.url, file, {
        timeout: 120000,
        headers: {
          "Content-Type": fileType,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setImageLoading(percentCompleted);
          if (percentCompleted === 100) {
            setTimeout(() => setImageLoading(""), 2000);
          }
        },
      });

      if (!afterUpload?.config?.url) throw new Error("url not found");
      const url = afterUpload.config.url.split("?")[0];
      const splitName = url.split("/");
      const name = splitName[splitName.length - 1];
      setValue(name);
      setImageLoading("");
    } catch (error: any) {
      console.error(error);
      setImageLoading("");
      setUploadError(
        error?.response?.data?.message || "ອັບໂຫຼດຮູບບໍ່ສຳເລັດ ລອງໃໝ່ອີກຄັ້ງ",
      );
    } finally {
      resetInput();
    }
  };

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const handlePickerKeyDown = (event: KeyboardEvent<HTMLLabelElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    openFilePicker();
  };

  const componentId = componentIdRef.current;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        ...rootStyle,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        id={"file-upload-" + componentId}
        onChange={handleUpload}
        accept={acceptedImageTypes}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
        tabIndex={-1}
      />
      <div style={{ position: "relative", width: "auto", height: "auto" }}>
        <label
          htmlFor={"file-upload-" + componentId}
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.preventDefault();
            openFilePicker();
          }}
          onKeyDown={handlePickerKeyDown}
          style={{
            backgroundColor: "#E4E4E4E4",
            height: 160,
            width: 160,
            borderRadius: "10px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            ...style,
            border: showError ? "1px solid red" : "none",
          }}
        >
          {value && !imageLoading ? (
            <img
              src={imageSrc}
              style={{
                height: "100%",
                width: "100%",
                objectFit: "cover",
              }}
              {...other}
            />
          ) : (
            <div
              style={{
                display: "flex",
                height: "100%",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                textAlign: "center",
                fontSize: 12,
                padding: 10,
                gap: 10,
                color: "#ababab",
              }}
            >
              {imageLoading ? (
                <Spinner animation="border" variant="primary" />
              ) : (
                <FiImage
                  style={{ width: 32, height: "auto", color: "#cacaca" }}
                />
              )}

              <span>
                {imageLoading
                  ? "ກຳລັງອັບໂຫຼດ..."
                  : uploadError || commentText}
              </span>
            </div>
          )}
        </label>
        {showError || uploadError ? (
          <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
            {showError ? meta.error : uploadError}
          </div>
        ) : null}
      </div>
      {imageLoading ? (
        <div className="progress mt-2 w-full" style={{ height: 20 }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{
              width: `${imageLoading}%`,
              backgroundColor: COLOR_APP,
            }}
            aria-valuenow={Number(imageLoading)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {imageLoading}%
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UploadImageFormik;
